import React, { useCallback, useEffect } from "react";
import * as Scrivito from "scrivito";
import { useFormikContext } from "formik";
import NiceModal from "@ebay/nice-modal-react";
import { useConfiguratorPopupContext } from "providers";
import { getHomepage } from "utils";
import { Button, ProductAddingModal } from "components";
import { useTranslate } from "hooks";
import { NodeKinds } from "interfaces/enums";
import { useLanguage } from "stores/globalStore";
import type { FormikCustomProductValues } from "interfaces/product/product.types";
import { useSetCopiedItem } from "stores/project";
import type { NodeApi } from "@hogartrabe/react-arborist-vrh";
import type { CustomProduct, Node } from "interfaces/schemas";

export const CustomProductHeader = Scrivito.connect(
  ({
    projectId,
    customProductId,
  }: {
    projectId: string;
    customProductId: string;
  }) => {
    const translate = useTranslate();
    const { values, validateForm } =
      useFormikContext<FormikCustomProductValues>();
    const lang = useLanguage();
    const setCopiedItem = useSetCopiedItem();
    const {
      setParams: updateParams,
      isUpdateButtonActive,
      showConfiguratorPopup,
    } = useConfiguratorPopupContext()!;

    useEffect(() => {
      updateParams(() => ({
        id: customProductId,
        kind: NodeKinds.CUSTOM_PRODUCT,
        orderCode: null,
        productName: values.title,
        productDescription: values.description,
        priceGroup: values.priceGroup,
        presentation: values.ordercode,
        price: isNaN(parseFloat(String(values.price)))
          ? 0.0
          : parseFloat(values.price) * 100,
        languageCode: lang,
      }));
    }, [customProductId, updateParams, values, lang]);

    const navigateToProjectConfigurator = useCallback(() => {
      const projectCongifuratorPage = getHomepage()?.get(
        "projectConfiguratorPage",
      ) as Scrivito.Obj;
      Scrivito.navigateTo(projectCongifuratorPage!, {
        projectId,
      });
    }, [projectId]);

    const handleNavigateBackToProject = useCallback(async () => {
      if (isUpdateButtonActive && customProductId) {
        showConfiguratorPopup(navigateToProjectConfigurator);
      } else {
        navigateToProjectConfigurator();
      }
    }, [
      isUpdateButtonActive,
      navigateToProjectConfigurator,
      showConfiguratorPopup,
      customProductId,
    ]);

    const handleAddToProject = useCallback(async () => {
      const errors = await validateForm();
      if (Object.keys(errors).length === 0) {
        const customProductNode = {
          id: "dummy-product-id",
          parentId: "dummy-parent-id",
          position: 0,
          nodes: null,
          selectedAccessories: null,
          kind: NodeKinds.CUSTOM_PRODUCT,
          active: true,
          partiallyVisible: false,
          productName: values.title,
          productDescription: values.description,
          orderCode: null,
          priceGroup: values.priceGroup,
          price: isNaN(parseFloat(String(values.price)))
            ? 0.0
            : parseFloat(values.price) * 100,
          presentation: values.ordercode,
          quantity: 1,
          pimId: null,
        } as CustomProduct;
        const customProductNodeApi = {
          data: customProductNode,
        } as NodeApi<Node>;
        setCopiedItem(customProductNodeApi);
        NiceModal.show(ProductAddingModal);
      }
    }, [
      validateForm,
      values.title,
      values.description,
      values.priceGroup,
      values.price,
      values.ordercode,
      setCopiedItem,
    ]);

    const headerText =
      typeof translate("CUSTOM_ITEM") === "string"
        ? (translate("CUSTOM_ITEM") as string).toUpperCase()
        : translate("CUSTOM_ITEM");

    return (
      <div className="d-flex flex-row flex-shrink-1 w-100 justify-content-between mb-4">
        <div className="d-flex gap-2 align-items-center">
          <h5>{headerText}</h5>
        </div>
        <div className="d-flex mb-2_5">
          <Button
            data-testid="product-configurator-header_back-to-project-structure-btn"
            className="ms-2"
            onClick={handleNavigateBackToProject}
            disabled={!projectId}
          >
            {translate("PRODUCT_CONFIGURATOR_BACK_TO_PROJECT_STRUCTURE")}
          </Button>
          <Button
            color="primary"
            className="ms-2"
            data-testid="custom_item_add_to_project"
            onClick={handleAddToProject}
            type="submit"
          >
            {translate("ADD_TO_PROJECT")}
          </Button>
        </div>
      </div>
    );
  },
);
